import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("gl_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Attach the current site language so backend transactional emails
  // (welcome, newsletter, order confirmation, waitlist, password reset)
  // render in the same language the user is browsing in.
  const lang = localStorage.getItem("gl_lang");
  if (lang) config.headers["Accept-Language"] = lang;
  // Pre-launch site gate — attach the unlock token (if the visitor has
  // entered the pre-launch password) so backend middleware allows the call.
  const unlock = localStorage.getItem("gl_site_unlock");
  if (unlock) config.headers["X-Site-Unlock"] = unlock;
  return config;
});

// ── Pre-launch site gate ──────────────────────────────────────────
export const siteStatus = () => api.get("/site/status").then((r) => r.data);
export const siteUnlock = (password) =>
  api.post("/site/unlock", { password }).then((r) => r.data);

// ── Products ───────────────────────────────────────────────────────
export const listProducts = (category, opts = {}) => {
  const params = {};
  if (category && category !== "all") params.category = category;
  if (opts.trending) params.trending = true;
  if (opts.limit) params.limit = opts.limit;
  return api.get("/products", { params }).then((r) => r.data);
};

export const getProduct = (slug) => api.get(`/products/${slug}`).then((r) => r.data);
export const trackView = (slug) => api.post(`/products/${slug}/view`).then((r) => r.data);

// ── Reviews ────────────────────────────────────────────────────────
export const listReviews = (slug) => api.get(`/products/${slug}/reviews`).then((r) => r.data);
export const postReview = (slug, payload) => api.post(`/products/${slug}/reviews`, payload).then((r) => r.data);

// ── Auth ───────────────────────────────────────────────────────────
export const register = (payload) => api.post("/auth/register", payload).then((r) => r.data);
export const login = (payload) => api.post("/auth/login", payload).then((r) => r.data);
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const exchangeGoogleSession = (sessionId) =>
  api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data);
export const requestPasswordReset = (email) =>
  api.post("/auth/password-reset/request", { email }).then((r) => r.data);
export const confirmPasswordReset = (token, new_password) =>
  api.post("/auth/password-reset/confirm", { token, new_password }).then((r) => r.data);

// ── Waitlist + related ─────────────────────────────────────────────
export const listRelatedProducts = (slug, limit = 4) =>
  api.get(`/products/${slug}/related`, { params: { limit } }).then((r) => r.data);
export const joinWaitlist = (slug, variant_name, email) =>
  api.post(`/products/${slug}/waitlist`, { product_slug: slug, variant_name, email }).then((r) => r.data);

// ── Newsletter ─────────────────────────────────────────────────────
export const subscribeNewsletter = (email, source = "footer") =>
  api.post("/newsletter/subscribe", { email, source }).then((r) => r.data);

// ── Admin: email templates ─────────────────────────────────────────
export const adminListEmailTemplates = (locale) =>
  adminApi.get("/admin/email-templates", { params: locale ? { locale } : {} }).then((r) => r.data);
export const adminUpdateEmailTemplate = (key, payload, locale) =>
  adminApi.put(`/admin/email-templates/${key}`, payload, { params: locale ? { locale } : {} }).then((r) => r.data);
export const adminResetEmailTemplate = (key, locale) =>
  adminApi.delete(`/admin/email-templates/${key}`, { params: locale ? { locale } : {} }).then((r) => r.data);
export const adminSendTestEmail = (key, to, locale) =>
  adminApi.post(
    `/admin/email-templates/${key}/test`,
    { to },
    { headers: locale ? { "Accept-Language": locale } : {} },
  ).then((r) => r.data);

// ── Journal ────────────────────────────────────────────────────────
export const listJournal = () => api.get("/journal").then((r) => r.data);
export const getJournal = (slug) => api.get(`/journal/${slug}`).then((r) => r.data);
export const adminListJournal = () => adminApi.get("/admin/journal").then((r) => r.data);
export const adminCreateJournal = (payload) => adminApi.post("/admin/journal", payload).then((r) => r.data);
export const adminUpdateJournal = (slug, payload) => adminApi.put(`/admin/journal/${slug}`, payload).then((r) => r.data);
export const adminDeleteJournal = (slug) => adminApi.delete(`/admin/journal/${slug}`).then((r) => r.data);

// ── Checkout ───────────────────────────────────────────────────────
export const createCheckout = (payload) => api.post("/checkout/session", payload).then((r) => r.data);
export const getCheckoutStatus = (sessionId) =>
  api.get(`/checkout/status/${sessionId}`).then((r) => r.data);
export const validateVoucher = (code) =>
  api.post("/vouchers/validate", { code }).then((r) => r.data);

// ── Admin (backoffice) ─────────────────────────────────────────────
export const ADMIN_TOKEN_KEY = "gl_admin_token";

export const adminApi = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});
adminApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  // Locale hint used by the "Send test email" preview endpoint so admins
  // can inspect the exact copy customers will receive in each language.
  const lang = localStorage.getItem("gl_lang");
  if (lang) config.headers["Accept-Language"] = lang;
  // Pre-launch gate token — admins still need this to reach /api/admin/*.
  const unlock = localStorage.getItem("gl_site_unlock");
  if (unlock) config.headers["X-Site-Unlock"] = unlock;
  return config;
});

export const adminLogin = (password) =>
  adminApi.post("/admin/login", { password }).then((r) => r.data);
export const adminListProducts = () =>
  adminApi.get("/admin/products").then((r) => r.data);
export const adminCreateProduct = (payload) =>
  adminApi.post("/admin/products", payload).then((r) => r.data);
export const adminUpdateProduct = (slug, payload) =>
  adminApi.put(`/admin/products/${slug}`, payload).then((r) => r.data);
export const adminReorderProducts = (slugs) =>
  adminApi.put("/admin/products/reorder", { slugs }).then((r) => r.data);
export const adminDeleteProduct = (slug) =>
  adminApi.delete(`/admin/products/${slug}`).then((r) => r.data);
export const adminUploadFile = (file) => {
  const form = new FormData();
  form.append("file", file);
  return adminApi
    .post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } })
    .then((r) => r.data);
};

// ─── Editable content + site settings ─────────────────────────────
export const getContent = () => api.get("/content").then((r) => r.data);
export const adminListContent = () => adminApi.get("/admin/content").then((r) => r.data);
export const adminSaveContentKey = (key, payload) =>
  adminApi.put(`/admin/content/${encodeURIComponent(key)}`, payload).then((r) => r.data);
export const adminUpdateSettings = (payload) =>
  adminApi.put("/admin/settings", payload).then((r) => r.data);

// ─── Categories ──────────────────────────────────────────────────
export const listCategories = () => api.get("/categories").then((r) => r.data);
export const adminListCategories = () => adminApi.get("/admin/categories").then((r) => r.data);
export const adminCreateCategory = (payload) => adminApi.post("/admin/categories", payload).then((r) => r.data);
export const adminUpdateCategory = (slug, payload) => adminApi.put(`/admin/categories/${slug}`, payload).then((r) => r.data);
export const adminReorderCategories = (slugs) => adminApi.put("/admin/categories/reorder", { slugs }).then((r) => r.data);
export const adminDeleteCategory = (slug) => adminApi.delete(`/admin/categories/${slug}`).then((r) => r.data);

// ─── Vouchers (admin-managed gift cards) ──────────────────────────
export const adminListVouchers = () =>
  adminApi.get("/admin/vouchers").then((r) => r.data);
export const adminCreateVoucher = (payload) =>
  adminApi.post("/admin/vouchers", payload).then((r) => r.data);
export const adminRenameVoucher = (code, newCode) =>
  adminApi.patch(`/admin/vouchers/${encodeURIComponent(code)}`, { code: newCode }).then((r) => r.data);
export const adminDeleteVoucher = (code) =>
  adminApi.delete(`/admin/vouchers/${encodeURIComponent(code)}`).then((r) => r.data);


// ─── Order management ──────────────────────────────────────────────
export const adminListOrders = (status) =>
  adminApi.get("/admin/orders", { params: status ? { status } : {} }).then((r) => r.data);
export const adminGetOrder = (id) =>
  adminApi.get(`/admin/orders/${id}`).then((r) => r.data);
export const adminUpdateOrder = (id, payload) =>
  adminApi.put(`/admin/orders/${id}`, payload).then((r) => r.data);
export const adminRefundOrder = (id, payload = {}) =>
  adminApi.post(`/admin/orders/${id}/refund`, payload).then((r) => r.data);
export const adminSendInvoice = (id, file) => {
  // multipart/form-data — `file` is optional; when omitted the email
  // still sends (branded template + order summary) with no attachment.
  const fd = new FormData();
  if (file) fd.append("invoice", file);
  return adminApi
    .post(`/admin/orders/${id}/send-invoice`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};
export const adminGetStats = () =>
  adminApi.get("/admin/stats").then((r) => r.data);
export const adminGetAnalytics = () =>
  adminApi.get("/admin/analytics").then((r) => r.data);
export const adminGetTrafficSummary = (days = 30) =>
  adminApi.get(`/admin/analytics/summary?days=${days}`).then((r) => r.data);
export const adminGetTrafficFunnel = (days = 30) =>
  adminApi.get(`/admin/analytics/funnel?days=${days}`).then((r) => r.data);
export const adminGetProductPopularity = (days = 30) =>
  adminApi.get(`/admin/analytics/products?days=${days}`).then((r) => r.data);
export const adminGetAbandonedCheckouts = (days = 30) =>
  adminApi.get(`/admin/analytics/abandoned-checkouts?days=${days}`).then((r) => r.data);
export const adminGetProductStats = (days = 7) =>
  adminApi.get(`/admin/products/stats?days=${days}`).then((r) => r.data);
export const trackPageView = (payload) =>
  api.post("/track", payload).catch(() => null);

// Customer-facing (public status page + logged-in order history)
export const getOrderStatus = (sessionId) =>
  api.get(`/orders/status/${sessionId}`).then((r) => r.data);
export const getMyOrders = () =>
  api.get("/orders/me").then((r) => r.data);

